import { Link } from "react-router-dom";

export default function JumboLoanRequirements2026() {
    return (
        <>
            <p>
                A jumbo loan is any mortgage that exceeds the conforming loan limits set by Fannie Mae and Freddie Mac. In 2026,
                that limit is $806,500 in most of the country, and $1,209,750 in high-cost areas. If you're borrowing more than
                that to buy a home, you're in jumbo territory — and the rules are materially different from what you're used to
                seeing on conventional loans.
            </p>

            <h2>Why Jumbo Loans Are Stricter</h2>
            <p>
                Conventional loans can be packaged and sold to Fannie Mae or Freddie Mac, which gives lenders a secondary
                market to offload risk. Jumbo loans don't qualify for that. Lenders hold them on their own books or sell them to
                private investors, which means they take on the full default risk themselves. That's why jumbo underwriting is
                tighter across every dimension.
            </p>

            <h2>Credit Score Requirements</h2>
            <p>
                Most jumbo lenders require a minimum credit score of 700, with many requiring 720 or higher. Some portfolio
                lenders will go to 680 for particularly strong borrowers in other respects (large down payment, substantial
                liquid assets), but those are the exceptions.
            </p>
            <p>
                To access the best jumbo rates, you generally want 740+. The difference between a 700 and 760 score on a
                $900,000 jumbo loan at current rates can mean 0.25%–0.5% in rate — which is $150–$300/month in payment.
                Over 30 years, that's $54,000–$108,000.
            </p>
            <p>
                Use the <Link to="/calculator/jumbo-loan">jumbo loan calculator</Link> to see how rate differences translate
                to actual monthly payments and total interest on your loan amount.
            </p>

            <h2>Down Payment Requirements</h2>
            <p>
                Expect a minimum of 10%–20% down for most jumbo loans. The exact requirement depends on the loan amount and
                your overall financial profile:
            </p>
            <p>
                <strong>$806,500–$1.5M:</strong> Many lenders accept 10%–15% down for well-qualified borrowers.<br />
                <strong>$1.5M–$2.5M:</strong> Most lenders require 20% down.<br />
                <strong>$2.5M+:</strong> Often requires 25%–30% down, sometimes more.
            </p>
            <p>
                Unlike conventional loans, jumbo mortgages don't have a standard PMI option if you put less than 20% down.
                Some lenders will allow a lower down payment but adjust the rate upward instead. Others use a piggyback loan
                structure (an 80/10/10, for example) to avoid jumbo territory entirely.
            </p>

            <h2>Debt-to-Income Ratio</h2>
            <p>
                Conventional conforming loans allow DTI up to 45%–50% in some cases. Jumbo lenders are stricter — most cap at
                43%, and the strongest borrowers typically have DTI under 36%.
            </p>
            <p>
                DTI is calculated as your total monthly debt payments (including the proposed mortgage payment) divided by your
                gross monthly income.
            </p>
            <p>
                Example: You earn $18,000/month gross. Your proposed mortgage PITI (principal, interest, taxes, insurance) is
                $5,500. You have a $700 car payment and $400 in student loans. Total monthly debt: $6,600. DTI = $6,600 ÷
                $18,000 = 36.7%. Most jumbo lenders would approve that.
            </p>
            <p>
                If the same borrower also had $1,500 in monthly credit card minimums, DTI would be 45.6% — above most jumbo
                lender thresholds.
            </p>

            <h2>Cash Reserves</h2>
            <p>
                This is where jumbo underwriting really differs from conventional. Lenders typically require 6–18 months of
                mortgage payments (PITI) in liquid reserves after your down payment and closing costs.
            </p>
            <p>
                On a $1,000,000 home with a $900,000 loan, if your monthly PITI is $6,200, some lenders require 12 months of
                reserves — meaning $74,400 sitting in verified bank accounts or liquid investments, separate from your down
                payment.
            </p>
            <p>
                Retirement accounts typically count at 60%–70% of face value (discounted because of early withdrawal penalties).
                Investment accounts count at face value. Equity in other real estate generally does not count.
            </p>

            <h2>Income Documentation</h2>
            <p>
                Jumbo lenders verify income thoroughly. W-2 employees need at least two years of W-2s and recent pay stubs.
                Self-employed borrowers typically need two years of tax returns (personal and business) and may need a CPA
                letter verifying the business is ongoing.
            </p>
            <p>
                Variable income — bonuses, commissions, rental income — is usually averaged over two years. Bonuses that are
                received inconsistently may not be counted at all. If you rely on large annual bonuses to qualify, discuss this
                with a jumbo lender before you're under contract, not after.
            </p>

            <h2>Interest Rates on Jumbo Loans in 2026</h2>
            <p>
                Historically, jumbo loans carried slightly higher rates than conforming loans because of the lack of government
                backing. More recently, the relationship has been more variable. In 2026, rates on 30-year jumbo loans are
                running roughly in line with or occasionally below conforming rates, depending on lender.
            </p>
            <p>
                Rates for 30-year jumbo: approximately 7.0%–7.75% for well-qualified borrowers in early 2026.<br />
                Rates for 15-year jumbo: approximately 6.25%–7.0%.
            </p>
            <p>
                Shopping multiple lenders is more important with jumbo loans than with conventional — the spread between the
                best and worst offers is larger because there's no standardized secondary market to compress rates.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                Jumbo loans are available to well-qualified buyers, but the standards are real. Strong credit, substantial
                reserves, low DTI, and verified income are non-negotiable. If you're planning a purchase in jumbo territory,
                run your numbers early and talk to lenders before you're under contract. Use the{" "}
                <Link to="/calculator/jumbo-loan">jumbo loan calculator</Link> to model monthly payments, total interest, and
                how different down payment amounts affect your loan amount and rate eligibility.
            </p>
        </>
    );
}